import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, Search, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface Neighborhood {
  id: string;
  name: string;
  district: string;
  city: string;
  minOrderAmount: number;
  deliveryFee: number;
  isActive: boolean;
}

export default function AdminNeighborhoods() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Neighborhood>>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    name: '',
    district: 'Merkez',
    city: 'Isparta',
    minOrderAmount: 100,
    deliveryFee: 15,
    isActive: true,
  });

  const { data: neighborhoods = [], isLoading } = useQuery<Neighborhood[]>({
    queryKey: ['neighborhoods'],
    queryFn: async () => {
      const { data } = await axios.get('http://localhost:5000/api/neighborhoods');
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (payload: typeof newForm) => {
      const token = localStorage.getItem('access_token');
      await axios.post('http://localhost:5000/api/neighborhoods', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['neighborhoods'] });
      setIsAddModalOpen(false);
      setNewForm({
        name: '',
        district: 'Merkez',
        city: 'Isparta',
        minOrderAmount: 100,
        deliveryFee: 15,
        isActive: true,
      });
      toast.success('Yeni mahalle başarıyla eklendi!');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Mahalle eklenirken hata oluştu.');
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (neighborhood: Partial<Neighborhood> & { id: string }) => {
      const token = localStorage.getItem('access_token');
      await axios.put(`http://localhost:5000/api/neighborhoods/${neighborhood.id}`, neighborhood, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['neighborhoods'] });
      setEditingId(null);
      toast.success('Mahalle bilgileri güncellendi.');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Güncelleme başarısız.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('access_token');
      await axios.delete(`http://localhost:5000/api/neighborhoods/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['neighborhoods'] });
      toast.success('Mahalle listeden kaldırıldı.');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Silme işlemi başarısız.');
    }
  });

  const handleEdit = (n: Neighborhood) => {
    setEditingId(n.id);
    setEditForm(n);
  };

  const handleSave = () => {
    if (editingId && editForm) {
      updateMutation.mutate({ ...editForm, id: editingId } as any);
    }
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`"${name}" mahallesini silmek istediğinize emin misiniz?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newForm.name.trim()) {
      toast.error('Lütfen mahalle adını girin.');
      return;
    }
    createMutation.mutate(newForm);
  };

  const filteredNeighborhoods = neighborhoods.filter((n) =>
    n.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.district.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>📍 Mahalle Yönetimi</h1>
          <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '0.9rem' }}>
            Isparta teslimat bölgeleri, minimum sepet tutarları ve kurye gönderim ücretleri
          </p>
        </div>

        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Plus size={18} /> Yeni Mahalle Ekle
        </button>
      </div>

      {/* Search & Overview Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '360px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
          <input
            type="text"
            className="form-input"
            style={{ paddingLeft: '38px', width: '100%' }}
            placeholder="Mahalle veya ilçe ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', fontSize: '0.85rem' }}>
          <span style={{ background: 'var(--color-surface)', padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
            Toplam: <strong>{neighborhoods.length}</strong> mahalle
          </span>
          <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '6px 14px', borderRadius: '8px', fontWeight: 600 }}>
            Aktif: <strong>{neighborhoods.filter(n => n.isActive).length}</strong>
          </span>
        </div>
      </div>

      {/* Table Card */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-2)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                <th style={{ padding: '14px 16px', fontWeight: 600 }}>Mahalle Adı</th>
                <th style={{ padding: '14px 16px', fontWeight: 600 }}>İlçe / İl</th>
                <th style={{ padding: '14px 16px', fontWeight: 600 }}>Min Sipariş</th>
                <th style={{ padding: '14px 16px', fontWeight: 600 }}>Gönderim Ücreti</th>
                <th style={{ padding: '14px 16px', fontWeight: 600 }}>Hizmet Durumu</th>
                <th style={{ padding: '14px 16px', fontWeight: 600, textAlign: 'right' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center' }}>Yükleniyor...</td></tr>
              ) : filteredNeighborhoods.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    Mahalle bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredNeighborhoods.map((n) => (
                  <tr key={n.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '14px 16px', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={16} color="var(--color-primary)" />
                        {n.name}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--color-text-secondary)' }}>
                      {n.district} / {n.city}
                    </td>

                    {editingId === n.id ? (
                      <>
                        <td style={{ padding: '10px 16px' }}>
                          <input
                            type="number"
                            className="form-input"
                            style={{ width: '100px', padding: '6px 10px' }}
                            value={editForm.minOrderAmount}
                            onChange={(e) => setEditForm({ ...editForm, minOrderAmount: Number(e.target.value) })}
                          />
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <input
                            type="number"
                            className="form-input"
                            style={{ width: '90px', padding: '6px 10px' }}
                            value={editForm.deliveryFee}
                            onChange={(e) => setEditForm({ ...editForm, deliveryFee: Number(e.target.value) })}
                          />
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <select
                            className="form-input"
                            style={{ padding: '6px 10px' }}
                            value={editForm.isActive ? 'true' : 'false'}
                            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'true' })}
                          >
                            <option value="true">Aktif (Hizmet Veriliyor)</option>
                            <option value="false">Pasif (Kapalı)</option>
                          </select>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={handleSave} className="btn btn-sm btn-primary" disabled={updateMutation.isPending}>
                              <Save size={14} /> Kaydet
                            </button>
                            <button onClick={() => setEditingId(null)} className="btn btn-sm btn-secondary">
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--color-primary)' }}>
                          ₺{n.minOrderAmount}
                        </td>
                        <td style={{ padding: '14px 16px', fontWeight: 600 }}>
                          {n.deliveryFee > 0 ? `₺${n.deliveryFee}` : 'Ücretsiz'}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          {n.isActive ? (
                            <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                              ● Hizmet Veriliyor
                            </span>
                          ) : (
                            <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                              ● Hizmet Kapalı
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleEdit(n)} className="btn btn-sm btn-secondary" title="Düzenle">
                              <Edit2 size={14} /> Düzenle
                            </button>
                            <button onClick={() => handleDelete(n.id, n.name)} className="btn btn-sm btn-danger" title="Sil" style={{ color: 'var(--color-error)', background: 'rgba(239, 68, 68, 0.1)', border: 'none' }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Yeni Mahalle Ekleme Modal */}
      {isAddModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '20px'
        }}>
          <div className="card animate-fade-in" style={{ width: '100%', maxWidth: '500px', padding: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MapPin size={22} color="var(--color-primary)" />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Yeni Mahalle Ekle</h2>
              </div>
              <button onClick={() => setIsAddModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label className="form-label">Mahalle Adı *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Örn: Sanayi Mahallesi"
                  required
                  value={newForm.name}
                  onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="form-label">İlçe</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newForm.district}
                    onChange={(e) => setNewForm({ ...newForm, district: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">İl</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newForm.city}
                    onChange={(e) => setNewForm({ ...newForm, city: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="form-label">Min. Sipariş Tutarı (₺)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    value={newForm.minOrderAmount}
                    onChange={(e) => setNewForm({ ...newForm, minOrderAmount: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="form-label">Gönderim Ücreti (₺)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    value={newForm.deliveryFee}
                    onChange={(e) => setNewForm({ ...newForm, deliveryFee: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                <input
                  type="checkbox"
                  id="isActiveNew"
                  checked={newForm.isActive}
                  onChange={(e) => setNewForm({ ...newForm, isActive: e.target.checked })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
                />
                <label htmlFor="isActiveNew" style={{ fontSize: '0.9rem', cursor: 'pointer', margin: 0 }}>
                  Bu mahalleye anında hizmet verilmeye başlansın (Aktif)
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="btn btn-secondary">
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Ekleniyor...' : 'Kaydet ve Yayına Al'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
